import { upperFirst } from "lodash-es";
import {Service, service, inject} from "./di";
import {i18n} from "../i18n/I18n";
import {CategoryService} from "./CategoryService";
import {LocationService} from "./LocationService";
import { SeoService } from "./SeoService";
import { LinkService } from "./LinkService";
import { Flyer } from "../definitions/FlyerDefinitions";
import { FlyerService } from "./FlyerService";
import { CouponService } from "./CouponService";

export interface IBreadcrumb {
	path: string;
	label: string;
	shouldReplacePath?: boolean;
}

@service("breadcrumbsService")
export default class BreadcrumbService extends Service {
	@inject categoryService: CategoryService;
	@inject locationService: LocationService;
	@inject seoService: SeoService;
	@inject linkService: LinkService;
	@inject flyerService: FlyerService;
	@inject couponService: CouponService;

	data: any;

	getBreadcrumbsPaths = (): string[] => ([
		"/item",
		"/produit",
		"/coupon"
	])

	async getBreadcrumbs() {
		const pathname = window.location && window.location.pathname ? window.location.pathname : "/";
		if (this.useNewBreadcrumbs(pathname, this.locationService.locale, this.seoService.inCity)) {
			return this.parseBreadcrumbs(pathname, this.locationService.locale, this.seoService.inCity);
		}

		return this.getLegacyBreadcrumbs(this.seoService.inCity);
	}

	async parseBreadcrumbs(pathname: string, locale: string, inCity: string): Promise<IBreadcrumb[]> {
		const pathes = pathname.split("/");
		const breadcrumbs = [
			{path: "/", label: "Home"},
			{path: `/${locale}`.toLowerCase(), label: i18n("br_home")},
		];

		if (pathes.length >= 3) {
			breadcrumbs.push({
				path: `/${locale}/${inCity}`.toLowerCase(),
				label: this.getLocationLabel(inCity, false)
			});
		}

		if (this.isItem(pathname)
			|| this.isFlyers(pathname)
			|| this.isFlyer(pathname)
			|| this.isCoupons(pathname)
			|| this.isCoupon(pathname)
		) {
			breadcrumbs.push({
				path: this.getTypePath(pathname, locale, inCity),
				label: this.getTypeLabel(pathname)
			});
		}

		if (this.isFlyer(pathname) || this.isItemLevel(pathname)) {
			breadcrumbs.push({
				path: await this.getParentTypePath(pathname),
				label: await this.getParentTypeLabel(pathname)
			});
		}

		if (this.isItemLevel(pathname)) {
			breadcrumbs.push({
				path: this.getItemLevelUrl(pathname),
				label: this.getCurrentPathLabel()
			});
		}

		if (this.isSearch(pathname)) {
			breadcrumbs.push({
				path: pathname,
				label: this.getSearchLabel(pathname)
			});
		}

		return breadcrumbs;
	}

	isItem(pathname: string): boolean {
		return pathname.includes("/item") || pathname.includes("produit");
	}

	isCoupon(pathname: string): boolean {
		return pathname.includes("/coupon") && !this.isCoupons(pathname);
	}


	isCoupons(pathname: string): boolean {
		return pathname.includes("/coupons");
	}

	isFlyers(pathname: string): boolean {
		return pathname.includes("/flyers")
			|| pathname.includes("/circulaires")
			|| pathname.includes("weekly_ads");
	}

	isFlyer(pathname: string): boolean {
		return pathname.includes("/flyer/")
			|| pathname.includes("/circulaire/")
			|| pathname.includes("weekly_ad/");
	}

	isSearch(pathname: string): boolean {
		return pathname.includes("/search/");
	}

	isItemLevel(pathname: string): boolean {
		return this.isItem(pathname) || this.isCoupon(pathname);
	}

	getItemLevelUrl(pathname: string): string {
		if (this.isCoupon(pathname)) {
			return `${pathname}?in-city=${this.locationService.getInCity()}&locale=${this.locationService.locale}`;
		}

		return pathname;
	}

	async getParentTypePath(pathname: string): Promise<string> {
		let flyer: Flyer;
		if (this.isCoupon(pathname) && this.data.matchUps.length > 0) {
			flyer = new Flyer(this.data.matchUps[0].flyer);
		} else if (this.isFlyer(pathname)) {
			flyer = new Flyer(this.data);
		} else if (this.isItem(pathname)) {
			const data = await this.flyerService.getFullFlyer(this.data.flyerId);
			flyer = new Flyer(data.flyer);
		}

		return this.linkService.linkTo(flyer);
	}

	async getParentTypeLabel(pathname: string) {
		let name = "";
		if (this.isItem(pathname) || this.isFlyer(pathname)) {
			name = await this.getFlyerName(this.data.flyerId ? this.data.flyerId : this.data.id);
		} else if (this.isCoupon(pathname) && this.data.matchUps.length > 0) {
			name = await this.getFlyerName(this.data.matchUps[0].flyer.id);
		}
		return name;
	}

	async getFlyerName(flyerId: number): Promise<string> {
		const { flyer } = await this.flyerService.getFullFlyer(flyerId);
		return `${flyer.merchant} ${flyer.name}`;
	}

	async getCouponName(couponId: number): Promise<string> {
		const coupon = await this.couponService.getCoupon(couponId);
		return `${coupon.merchantName}`;
	}

	getCurrentPathLabel = () => this.data.name;

	getTypePath(pathname: string, locale: string, inCity: string) {
		const path = `/${locale}/${inCity}`.toLowerCase();
		let type = "";
		if (this.isItem(pathname) || this.isFlyers(pathname) || this.isFlyer(pathname)) {
			if (locale.includes("fr")) {
				type = "circulaires";
			} else if (locale.includes("us")) {
				type = "weekly_ads";
			} else {
				type = "flyers";
			}
		} else if (this.isCoupon(pathname) || this.isCoupons(pathname)) {
			type = "coupons";
		}
		return `${path}/${type}`;
	}

	getTypeLabel(pathname: string) {
		let label = "";
		if (this.isFlyer(pathname) || this.isFlyers(pathname) || this.isItem(pathname)) {
			label = i18n("flyers_header_category");
		} else if (this.isCoupon(pathname) || this.isCoupons(pathname)) {
			label = i18n("coupons");
		}

		return label;
	}

	useNewBreadcrumbs(pathname: string, locale: string, inCity: string): boolean {
		return (
			pathname.includes(locale)
		) || ( pathname.includes("/coupon/") && Boolean(inCity));
	}

	getLegacyBreadcrumbs(inCity: string) {
		const pathname = window.location && window.location.pathname ? window.location.pathname : "/";
		const pathes = pathname.split("/");
		const breadcrumbs: IBreadcrumb[] = [{path: "/", label: "Home"}];
		let breadcrumbPath = "";
		pathes.shift();
		pathes.forEach((path) => {
			let label = this.getLabel(pathname, path, inCity);
			if (this.seoService.isLocale(path)) {
				return;
			}
			breadcrumbPath += `/${this.getPath(path)}`;
			breadcrumbs.push({
				...this.prepareBreadcrumbPath(breadcrumbPath, inCity),
				label
			});
		});

		return this.parseLegacy(breadcrumbs);
	}

	parseLegacy(breadcrumbs: IBreadcrumb[]): IBreadcrumb[] {
		for (let i = 0; i < breadcrumbs.length; i++) {
			if (breadcrumbs[i].shouldReplacePath && breadcrumbs[i + 1]) {
				breadcrumbs[i].path = breadcrumbs[i + 1].path;
			}
		}

		return breadcrumbs;
	}

	prepareBreadcrumbPath(
		path: string,
		inCity: string
	): {path: string, shouldReplacePath?: boolean} {
		if (path === `/${inCity}`) {
			return {path: path.toLowerCase(), shouldReplacePath: true};
		}

		return {path: path.toLowerCase()};
	}

	getPath(path: string): string {
		if (path === "flyer" || path === "item") {
			return "flyers";
		}

		return path;
	}

	getLabel(pathname: string, path: string, inCity: string) {
		let label = i18n(path);
		if (!label) {
			label = this.getCategoryLabel(path);
		}

		if (inCity === path) {
			label = this.getLocationLabel(path);
		}

		if (pathname.indexOf("search") !== -1) {
			label = path;
		}

		if (!label) {
			label = this.getItemLabel();
		}

		if (!label) {
			label = path;
		}

		return label;
	}

	getLocationLabel(path: string, includeState: boolean = true): string {
		const city = path.split("-");
		const state = city.pop();
		const parsedState: string = this.locationService.getStateToStateTextMapping()[state] ?
			this.locationService.getStateToStateTextMapping()[state] :
			state.toUpperCase();
		const parsedCity = `${city.map(c => upperFirst(c)).join(" ")}`;
		return  `${parsedCity}${includeState ? ` ${parsedState}` : ``}`;
	}

	getItemLabel(): string | null {
		return this.data ? this.data.name : null;
	}

	getSearchLabel(pathname: string): string {
		return upperFirst(pathname.split("/").pop());
	}

	getCategoryLabel(path: string): string {
		const mapping = this.categoryService.getCategoySlugToNameMapping();
		return i18n(mapping[path]);
	}

	setData(data: any) {
		this.data = data;
	}

	getBrowserBreadcrumbs () {
		const pathes = window.location.pathname.split("/");

		const breadcrumbs = [];
		for (let i = 0; i < pathes.length; i++) {
			const el = pathes.pop();
			if (el === "browser" || el === "categories") break;

			const label = pathes[pathes.length - 1];
			breadcrumbs.push({
				path: pathes.join("/"),
				label: label === "browser" ? "Locations" : upperFirst(label)
			});
		}

		breadcrumbs.push({path: "/", label: "Home"});
		return breadcrumbs.reverse();
	}

	getCurrentLocation() {
		const pathes = window.location.pathname.split("/");
		return upperFirst(pathes.pop());
	}
}

